import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { invalidateSchoolSettings } from '../utils/schoolSettings';
import { InputSwitch } from 'primereact/inputswitch';
import MediaPickerDialog from '../../components/Media/MediaPickerDialog';
import { SelectButton } from 'primereact/selectbutton';
import { Calendar } from 'primereact/calendar';
import {
  TIMING_MODE_OPTIONS,
  DEFAULT_SEASONAL,
  normaliseTimingConfig,
} from '../utils/classTiming';
import '../../components/Media/media.scss';

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleName = user?.roleName || user?.role?.name || '';
  const isSchoolAdmin = roleName === 'Admin' || roleName === 'SuperAdmin';

  const [profile, setProfile] = useState({
    schoolName: '',
    adminEmail: '',
    adminName: '',
    email: '',
    phone: '',
    address: '',
    country: '',
    currency: '',
    logo: '',
    primaryColor: '',
    termsEnabled: false,
    feeMode: 'monthly',
    workingDays: [1, 2, 3, 4, 5],
    partialWeekBilling: 'full',
    timingMode: 'per_schedule',
    timingConfig: normaliseTimingConfig({ seasonal: DEFAULT_SEASONAL }),
  });
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);

  // "MM-DD" <-> Date (year is irrelevant; the switch recurs annually).
  const mdToDate = (md) => {
    const m = String(md || '').match(/^(\d{2})-(\d{2})$/);
    return m ? new Date(2000, Number(m[1]) - 1, Number(m[2])) : null;
  };
  const dateToMd = (d) => {
    if (!(d instanceof Date)) return null;
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  // Immutably patch the nested seasonal config.
  const patchSeasonal = (path, value) =>
    setProfile((prev) => {
      const cfg = normaliseTimingConfig(prev.timingConfig || { seasonal: DEFAULT_SEASONAL });
      const seasonal = { ...cfg.seasonal };
      if (path === 'summerStart' || path === 'winterStart') {
        seasonal[path] = value;
      } else {
        const [band, field] = path.split('.');
        seasonal[band] = { ...seasonal[band], [field]: value };
      }
      return { ...prev, timingConfig: { seasonal } };
    });

  // 0 = Sunday … 6 = Saturday, matching Date#getDay on both sides.
  const WORKING_DAY_OPTIONS = [
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
    { label: 'Sun', value: 0 },
  ];

  const PARTIAL_WEEK_OPTIONS = [
    {
      label: 'Charge the full week',
      value: 'full',
      hint: 'A week that runs even one day bills as a whole week.',
    },
    {
      label: 'Charge pro-rata',
      value: 'pro_rata',
      hint: 'A week shortened by holidays bills for the days it actually ran.',
    },
  ];

  const FEE_MODE_OPTIONS = [
    { label: 'Monthly — one voucher per student per month', value: 'monthly' },
    { label: 'Weekly per term — rate × available weeks in the term', value: 'weekly_per_term' },
  ];

  const CURRENCY_OPTIONS = [
    { label: 'PKR — Pakistani Rupee (Rs)', value: 'Rs' },
    { label: 'USD — US Dollar ($)', value: '$' },
    { label: 'GBP — British Pound (£)', value: '£' },
    { label: 'EUR — Euro (€)', value: '€' },
    { label: 'AED — UAE Dirham (AED)', value: 'AED' },
    { label: 'SAR — Saudi Riyal (SAR)', value: 'SAR' },
    { label: 'INR — Indian Rupee (₹)', value: '₹' },
    { label: 'BDT — Bangladeshi Taka (৳)', value: '৳' },
    { label: 'None (numbers only)', value: '' },
  ];
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    axios.get('/api/schools/my-settings')
      .then(res => {
        if (res.data.success) setProfile(res.data.data);
      })
      .catch(err => console.error('Failed to load school settings:', err))
      .finally(() => setFetching(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (profile.primaryColor && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(profile.primaryColor.trim())) {
      toast.error('Brand color must be a hex value like #6366f1', { autoClose: 3000 });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.put('/api/schools/my-settings', profile);
      if (res.data.success) {
        invalidateSchoolSettings(); // currency / feature flags may have changed
        const weeks = res.data.weeksUpdated;
        toast.success(
          weeks
            ? `Settings saved — ${weeks} term week(s) recalculated`
            : res.data.message || 'Settings saved',
          { autoClose: 2500 }
        );
      } else {
        toast.error(res.data.message || 'Save failed', { autoClose: 3000 });
      }
    } catch {
      toast.error('Error updating settings');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="p-4 flex align-items-center gap-2">
        <i className="pi pi-spin pi-spinner" />
        <span>Loading settings…</span>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-2xl font-bold m-0">School Settings</h2>
        <p className="text-color-secondary mt-1">Manage your school profile and preferences</p>
      </div>

      <TabView>
        {/* ── School Profile (Admin only) ── */}
        {isSchoolAdmin && (
          <TabPanel header="School Profile" leftIcon="pi pi-building mr-2">
            <Card>
              <div className="grid formgrid">
                <div className="field col-12 md:col-6">
                  <label htmlFor="schoolName">School Name</label>
                  <InputText id="schoolName" name="schoolName" value={profile.schoolName} onChange={handleChange} className="w-full" />
                </div>
                <div className="field col-12 md:col-6">
                  <label htmlFor="adminName">Admin Name</label>
                  <InputText id="adminName" name="adminName" value={profile.adminName} onChange={handleChange} className="w-full" />
                </div>
                <div className="field col-12 md:col-6">
                  <label htmlFor="adminEmail">Admin Login Email</label>
                  <InputText id="adminEmail" name="adminEmail" type="email" value={profile.adminEmail} onChange={handleChange} className="w-full" />
                </div>
                <div className="field col-12 md:col-6">
                  <label htmlFor="email">School Contact Email</label>
                  <InputText id="email" name="email" type="email" value={profile.email} onChange={handleChange} className="w-full" placeholder="contact@yourschool.edu" />
                </div>
                <div className="field col-12 md:col-6">
                  <label htmlFor="phone">Phone Number</label>
                  <InputText id="phone" name="phone" value={profile.phone} onChange={handleChange} className="w-full" placeholder="+1 234 567 8900" />
                </div>
                <div className="field col-12 md:col-6">
                  <label htmlFor="country">Country</label>
                  <InputText id="country" name="country" value={profile.country} onChange={handleChange} className="w-full" />
                </div>
                <div className="field col-12 md:col-6">
                  <label htmlFor="currency">Currency</label>
                  <Dropdown
                    id="currency"
                    name="currency"
                    value={profile.currency}
                    options={CURRENCY_OPTIONS}
                    onChange={(e) => setProfile((prev) => ({ ...prev, currency: e.value }))}
                    editable
                    placeholder="Select or type a currency symbol"
                    className="w-full"
                  />
                  <small className="text-color-secondary">Shown next to fees and amounts across the app.</small>
                </div>
                <div className="field col-12">
                  <label className="block mb-2">School Logo</label>
                  <div className="flex align-items-center gap-3 flex-wrap">
                    <div className="school-logo-preview">
                      {profile.logo
                        ? <img src={profile.logo} alt="School logo" />
                        : <i className="pi pi-image" />}
                    </div>
                    <div className="flex flex-column gap-2">
                      <Button
                        label={profile.logo ? 'Change logo' : 'Choose logo'}
                        icon="pi pi-images"
                        className="p-button-sm"
                        outlined
                        onClick={() => setLogoPickerOpen(true)}
                      />
                      {profile.logo && (
                        <Button
                          label="Remove"
                          icon="pi pi-times"
                          className="p-button-sm"
                          severity="danger"
                          text
                          onClick={() => setProfile(prev => ({ ...prev, logo: '' }))}
                        />
                      )}
                      <small className="text-color-secondary" style={{ maxWidth: '22rem' }}>
                        Picked from your Media Library. Shown in the portal sidebar and used
                        on your public website when no separate website logo is set. A square
                        or wide PNG around 200&nbsp;px tall works best.
                      </small>
                    </div>
                  </div>
                </div>
                <div className="field col-12">
                  <label className="block mb-2">Brand Color</label>
                  <div className="flex align-items-center gap-3 flex-wrap">
                    <input
                      type="color"
                      aria-label="Brand color"
                      value={profile.primaryColor || '#6366f1'}
                      onChange={(e) => setProfile(prev => ({ ...prev, primaryColor: e.target.value }))}
                      style={{ width: 44, height: 44, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                    />
                    <InputText
                      name="primaryColor"
                      value={profile.primaryColor || ''}
                      onChange={handleChange}
                      placeholder="#6366f1"
                      style={{ width: '9rem' }}
                    />
                    {profile.primaryColor && (
                      <Button
                        label="Reset to default"
                        icon="pi pi-times"
                        className="p-button-sm"
                        severity="secondary"
                        text
                        onClick={() => setProfile(prev => ({ ...prev, primaryColor: '' }))}
                      />
                    )}
                  </div>
                  <small className="text-color-secondary block mt-2" style={{ maxWidth: '30rem' }}>
                    The accent color used across your branded mobile app (buttons, tabs,
                    highlights). Leave empty to use the default theme.
                  </small>
                </div>
                <div className="field col-12">
                  <label htmlFor="address">Address</label>
                  <InputTextarea id="address" name="address" value={profile.address} onChange={handleChange} className="w-full" rows={3} placeholder="123 School St, City, State, ZIP" />
                </div>
              </div>
              <Divider />
              <Button label="Save Profile" icon="pi pi-check" loading={loading} onClick={handleSave} />
            </Card>
          </TabPanel>
        )}

        {/* ── Academic Setup (Admin only) ── */}
        {isSchoolAdmin && (
          <TabPanel header="Academic Setup" leftIcon="pi pi-calendar mr-2">
            <Card>
              <div className="flex align-items-start justify-content-between gap-3 p-3 surface-ground border-round">
                <div>
                  <div className="font-semibold">Academic Terms</div>
                  <small className="text-color-secondary">
                    Split each academic session into terms with their own dates, and mark
                    individual weeks available or off (holidays, closures). Leave this off to
                    keep sessions working exactly as they do today.
                  </small>
                </div>
                <InputSwitch
                  checked={profile.termsEnabled}
                  onChange={(e) =>
                    setProfile((prev) => ({
                      ...prev,
                      termsEnabled: e.value,
                      // Weekly billing has no meaning without terms
                      feeMode: e.value ? prev.feeMode : 'monthly',
                    }))
                  }
                />
              </div>

              {profile.termsEnabled && (
                <div className="p-3 mt-3 surface-ground border-round">
                  <div className="font-semibold mb-2">Fee Collection Mode</div>
                  <Dropdown
                    value={profile.feeMode}
                    options={FEE_MODE_OPTIONS}
                    onChange={(e) => setProfile((prev) => ({ ...prev, feeMode: e.value }))}
                    className="w-full"
                  />
                  <small className="text-color-secondary">
                    Weekly per term multiplies the weekly rate by the number of available weeks
                    in each term. Existing monthly vouchers are left untouched.
                  </small>
                </div>
              )}

              {/* School week — the input the holiday calendar works against */}
              <div className="p-3 mt-3 surface-ground border-round">
                <div className="font-semibold mb-2">Teaching Days</div>
                <SelectButton
                  multiple
                  value={profile.workingDays}
                  options={WORKING_DAY_OPTIONS}
                  onChange={(e) =>
                    setProfile((prev) => ({
                      // SelectButton clears to null when the last chip is
                      // deselected; an empty week would zero every fee.
                      ...prev,
                      workingDays: e.value?.length ? e.value : prev.workingDays,
                    }))
                  }
                />
                <small className="block mt-2 text-color-secondary">
                  Days your school teaches. The Holiday Calendar counts only these days,
                  so a closure landing on a weekend never shortens a week.
                </small>
              </div>

              {profile.termsEnabled && profile.feeMode === 'weekly_per_term' && (
                <div className="p-3 mt-3 surface-ground border-round">
                  <div className="font-semibold mb-2">Weeks Shortened by Holidays</div>
                  <Dropdown
                    value={profile.partialWeekBilling}
                    options={PARTIAL_WEEK_OPTIONS}
                    onChange={(e) =>
                      setProfile((prev) => ({ ...prev, partialWeekBilling: e.value }))
                    }
                    className="w-full"
                  />
                  <small className="block mt-1 text-color-secondary">
                    {PARTIAL_WEEK_OPTIONS.find((o) => o.value === profile.partialWeekBilling)?.hint}
                    {' '}Already-issued vouchers keep the figures they were created with.
                  </small>
                </div>
              )}

              {/* ── Class timings: how a class's start/end time is decided ── */}
              <div className="p-3 mt-3 surface-ground border-round">
                <div className="font-semibold mb-2">Class Timings</div>
                <SelectButton
                  value={profile.timingMode}
                  options={TIMING_MODE_OPTIONS}
                  onChange={(e) => {
                    if (!e.value) return; // don't allow clearing to null
                    setProfile((prev) => {
                      const next = { ...prev, timingMode: e.value };
                      // Seed sensible summer/winter defaults the first time
                      // seasonal is picked so nothing shows blank.
                      if (e.value === 'seasonal') {
                        const cfg = normaliseTimingConfig(
                          prev.timingConfig || { seasonal: DEFAULT_SEASONAL }
                        ).seasonal;
                        next.timingConfig = {
                          seasonal: {
                            summer: {
                              start: cfg.summer.start || DEFAULT_SEASONAL.summer.start,
                              end: cfg.summer.end || DEFAULT_SEASONAL.summer.end,
                            },
                            winter: {
                              start: cfg.winter.start || DEFAULT_SEASONAL.winter.start,
                              end: cfg.winter.end || DEFAULT_SEASONAL.winter.end,
                            },
                            summerStart: cfg.summerStart || DEFAULT_SEASONAL.summerStart,
                            winterStart: cfg.winterStart || DEFAULT_SEASONAL.winterStart,
                          },
                        };
                      }
                      return next;
                    });
                  }}
                />
                <small className="block mt-2 text-color-secondary">
                  {profile.timingMode === 'seasonal'
                    ? 'One daily session time that switches between summer and winter on the dates below. A term crossing the switch shows both.'
                    : profile.timingMode === 'per_term'
                    ? 'Every class in a term uses that term’s time. Set each term’s time under Manage Terms.'
                    : 'Each class keeps the start/end time entered on its schedule (default).'}
                </small>

                {profile.timingMode === 'seasonal' && (() => {
                  const s = normaliseTimingConfig(
                    profile.timingConfig || { seasonal: DEFAULT_SEASONAL }
                  ).seasonal;
                  const timeRow = (label, band) => (
                    <div className="flex align-items-center gap-2 flex-wrap mb-2">
                      <span style={{ width: 70 }} className="font-medium">{label}</span>
                      <InputText
                        type="time"
                        value={s[band].start || ''}
                        onChange={(e) => patchSeasonal(`${band}.start`, e.target.value)}
                      />
                      <span>to</span>
                      <InputText
                        type="time"
                        value={s[band].end || ''}
                        onChange={(e) => patchSeasonal(`${band}.end`, e.target.value)}
                      />
                      <span className="text-color-secondary">from</span>
                      <Calendar
                        value={mdToDate(band === 'summer' ? s.summerStart : s.winterStart)}
                        onChange={(e) =>
                          patchSeasonal(
                            band === 'summer' ? 'summerStart' : 'winterStart',
                            dateToMd(e.value)
                          )
                        }
                        dateFormat="d MM"
                        showIcon
                        readOnlyInput
                        style={{ width: 150 }}
                      />
                    </div>
                  );
                  return (
                    <div className="mt-3">
                      {timeRow('Summer', 'summer')}
                      {timeRow('Winter', 'winter')}
                      <small className="block text-color-secondary">
                        Switch dates repeat every year — no need to update them each session.
                      </small>
                    </div>
                  );
                })()}
              </div>

              <Divider />
              <div className="flex gap-2">
                <Button label="Save" icon="pi pi-check" loading={loading} onClick={handleSave} />
                {profile.termsEnabled && (
                  <Button
                    label="Manage Terms"
                    icon="pi pi-external-link"
                    className="p-button-secondary"
                    onClick={() => navigate('/index/sessions')}
                  />
                )}
              </div>
            </Card>
          </TabPanel>
        )}

        {/* ── Notifications ── */}
        <TabPanel header="Notifications" leftIcon="pi pi-bell mr-2">
          <Card>
            <p className="text-color-secondary mb-3">
              Control which events trigger in-app notifications for each user type in your school.
            </p>
            <Button
              label="Manage Notification Preferences"
              icon="pi pi-arrow-right"
              iconPos="right"
              onClick={() => navigate('/index/notification-preferences')}
            />
          </Card>
        </TabPanel>

        {/* ── Access Control (Admin only) ── */}
        {isSchoolAdmin && (
          <TabPanel header="Access Control" leftIcon="pi pi-shield mr-2">
            <Card>
              <div className="flex flex-column gap-3">
                <div className="flex align-items-center justify-content-between p-3 surface-ground border-round">
                  <div>
                    <div className="font-semibold">Roles &amp; Permissions</div>
                    <small className="text-color-secondary">Define roles and control what each role can access</small>
                  </div>
                  <Button
                    label="Manage Roles"
                    icon="pi pi-external-link"
                    className="p-button-sm p-button-secondary"
                    onClick={() => navigate('/index/roles')}
                  />
                </div>
                <div className="flex align-items-center justify-content-between p-3 surface-ground border-round">
                  <div>
                    <div className="font-semibold">User Accounts</div>
                    <small className="text-color-secondary">Add, edit, or deactivate staff and user accounts</small>
                  </div>
                  <Button
                    label="Manage Users"
                    icon="pi pi-external-link"
                    className="p-button-sm p-button-secondary"
                    onClick={() => navigate('/index/users')}
                  />
                </div>
              </div>
            </Card>
          </TabPanel>
        )}
      </TabView>

      <MediaPickerDialog
        visible={logoPickerOpen}
        onHide={() => setLogoPickerOpen(false)}
        typeLock="image"
        publicUse
        header="Choose a school logo"
        onSelect={(item) => setProfile(prev => ({ ...prev, logo: item.file_path }))}
      />
    </div>
  );
};

export default Settings;
